import { DataConnected, DataShare, Events, FlowData, Hospital, Queued, SendAlt, UserMultiple } from '@carbon/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { api, getToken, Permission, Session, SessionExpiredError, setToken } from './lib/api';
import { Shell } from './components/Shell';
import { Loading, ToastProvider, useToast } from './components/ui';
import { Login } from './pages/Login';
import { Overview } from './pages/Overview';
import { Submissions } from './pages/Submissions';
import { Queue } from './pages/Queue';
import { Audit } from './pages/Audit';
import { Facilities } from './pages/Facilities';
import { Targets } from './pages/Targets';
import { Mappings } from './pages/Mappings';
import { Users } from './pages/Users';

const PAGE_META: Record<
  string,
  {
    title: string;
    subtitle: string;
    eyebrow?: string;
    icon?: React.ComponentType<{ size?: number | string; className?: string }>;
  }
> = {
  '/': {
    eyebrow: 'Interoperability Hub',
    title: 'Overview',
    subtitle: 'Aggregate reporting from every connected facility',
    icon: DataShare,
  },
  '/queue': {
    eyebrow: 'Interoperability Hub',
    title: 'Queue',
    subtitle: 'Work waiting to be translated and delivered to DHIS2',
    icon: Queued,
  },
  '/audit': {
    eyebrow: 'Interoperability Hub',
    title: 'Activity log',
    subtitle: 'Who did what, from where, and what the hub did on its own',
    icon: Events,
  },
  '/submissions': {
    eyebrow: 'Interoperability Hub',
    title: 'Submissions',
    subtitle: 'Everything facilities have sent, and how it fared in DHIS2',
    icon: SendAlt,
  },
  '/facilities': {
    eyebrow: 'Interoperability Hub',
    title: 'Facilities',
    subtitle: 'Who may submit, and which organisation unit they report as',
    icon: Hospital,
  },
  '/targets': {
    eyebrow: 'Interoperability Hub',
    title: 'DHIS2 servers',
    subtitle: 'Where data is delivered, and with which credentials',
    icon: DataConnected,
  },
  '/mappings': {
    eyebrow: 'Interoperability Hub',
    title: 'Mappings',
    subtitle: 'How facility codes translate into DHIS2 identifiers',
    icon: FlowData,
  },
  '/users': {
    eyebrow: 'Interoperability Hub',
    title: 'Users',
    subtitle: 'Who can see and change this hub',
    icon: UserMultiple,
  },
};

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const user = session?.user ?? null;
  const [checking, setChecking] = useState(true);
  const location = useLocation();
  const toast = useToast();

  // A takeover notice is stashed at sign-in and shown once the shell is up.
  useEffect(() => {
    const notice = sessionStorage.getItem('ssemr.mediator.displaced');
    if (user && notice) {
      sessionStorage.removeItem('ssemr.mediator.displaced');
      toast('info', notice);
    }
  }, [user, toast]);

  const signOut = useCallback(() => {
    void api.post('/api/auth/logout').catch(() => undefined);
    setToken(null);
    setSession(null);
  }, []);

  /** Server-side checks are authoritative; this only decides what is worth showing. */
  const can = useCallback(
    (permission: Permission) => session?.permissions.includes(permission) ?? false,
    [session],
  );

  // Resume an existing session on load. A token that the server no longer accepts is dropped
  // rather than left to fail on the first real request.
  useEffect(() => {
    if (!getToken()) {
      setChecking(false);
      return;
    }
    api
      .get<Session>('/api/auth/me')
      .then(setSession)
      .catch((error) => {
        if (error instanceof SessionExpiredError) setToken(null);
      })
      .finally(() => setChecking(false));
  }, []);

  if (checking) {
    return (
      <div className="center-screen">
        <Loading label="Restoring session" />
      </div>
    );
  }

  if (!user) {
    return <Login onSignedIn={setSession} />;
  }

  const meta = PAGE_META[location.pathname] ?? { title: 'Not found', subtitle: '' };

  // A scoped account with nothing assigned would otherwise see a wall of empty tables and assume
  // the hub was broken.
  const noScope = session ? !session.scope.all && session.scope.facilityIds.length === 0 : false;

  return (
    <Shell
      user={user}
      can={can}
      onSignOut={signOut}
      eyebrow={meta.eyebrow}
      title={meta.title}
      subtitle={meta.subtitle}
      icon={meta.icon}
    >
      {noScope ? (
        <div className="banner banner-warn">
          <span>
            No facilities have been assigned to your account yet, so there is no data to show. Ask a
            super administrator to grant you access.
          </span>
        </div>
      ) : null}

      <Routes>
        <Route path="/" element={<Overview />} />
        <Route path="/queue" element={<Queue can={can} />} />
        <Route path="/audit" element={<Audit />} />
        <Route path="/submissions" element={<Submissions can={can} />} />
        <Route path="/facilities" element={<Facilities can={can} />} />
        <Route path="/targets" element={<Targets can={can} />} />
        <Route path="/mappings" element={<Mappings can={can} />} />
        <Route
          path="/users"
          element={
            can('users.read') ? (
              <Users currentUser={user} assignableRoles={session!.assignableRoles} />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Shell>
  );
}

export function Root() {
  return (
    <ToastProvider>
      <div className="aurora" aria-hidden="true" />
      <App />
    </ToastProvider>
  );
}
